import React, { useState, useEffect } from 'react';
import { Save } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import RichTextEditor from '../../components/admin/RichTextEditor';
import ImageUpload from '../../components/admin/ImageUpload';

const SettingsManagement = () => {
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    site_name_ar: '',
    site_name_en: '',
    home_settings: {
      hero_title_ar: '',
      hero_subtitle_ar: '',
      stats_programs: '10+',
      stats_faculty: '50+',
      stats_students: '1000+',
      stats_agreements: '5+',
    },
    contact_settings: {
      email: '',
      phone: '',
      address_ar: '',
      facebook: '',
      twitter: '',
      instagram: '',
    },
    about_ar: '',
    vision_ar: '',
    mission_ar: '',
  });

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

  useEffect(() => { fetchSettings(); }, []);

  const fetchSettings = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/settings/`);
      const data = await response.json();
      setFormData(data);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`${BACKEND_URL}/api/settings/`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify(formData),
      });
      if (response.ok) {
        alert('تم حفظ الإعدادات بنجاح');
      }
    } catch (error) {
      alert('حدث خطأ');
    }
  };

  if (loading) return <div>جاري التحميل...</div>;

  return (
    <div className="space-y-6">
      <h1 className="text-3xl font-bold text-[#0F388E]">إعدادات الموقع</h1>

      <form onSubmit={handleSubmit} className="space-y-6">
        <Card>
          <CardHeader><CardTitle>معلومات الموقع الأساسية</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <input type="text" placeholder="اسم الموقع" value={formData.site_name_ar}
              onChange={(e) => setFormData({...formData, site_name_ar: e.target.value})}
              className="w-full px-4 py-2 border rounded-lg" />
            
            <ImageUpload 
              label="🏛️ شعار الجامعة" 
              value={formData.site_logo || ''}
              onChange={(v) => setFormData({...formData, site_logo: v})} />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>📊 Google Analytics & Search Console</CardTitle>
            <p className="text-sm text-gray-600 mt-2">أدوات تحليل وتحسين محركات البحث</p>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Google Analytics Measurement ID
              </label>
              <input 
                type="text" 
                placeholder="G-XXXXXXXXXX" 
                value={formData.google_analytics_id || ''}
                onChange={(e) => setFormData({...formData, google_analytics_id: e.target.value})}
                className="w-full px-4 py-2 border rounded-lg font-mono text-sm" 
              />
              <p className="text-xs text-gray-500 mt-1">
                احصل عليه من: Google Analytics → Admin → Data Streams → Measurement ID
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Google Search Console Verification Code
              </label>
              <input 
                type="text" 
                placeholder="abc123xyz..." 
                value={formData.google_search_console_code || ''}
                onChange={(e) => setFormData({...formData, google_search_console_code: e.target.value})}
                className="w-full px-4 py-2 border rounded-lg font-mono text-sm" 
              />
              <p className="text-xs text-gray-500 mt-1">
                احصل عليه من: Google Search Console → Settings → Verification → HTML tag method
              </p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle>الصفحة الرئيسية</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <input type="text" placeholder="عنوان Hero" 
              value={formData.home_settings?.hero_title_ar || ''}
              onChange={(e) => setFormData({
                ...formData, 
                home_settings: {...formData.home_settings, hero_title_ar: e.target.value}
              })}
              className="w-full px-4 py-2 border rounded-lg" />
            <input type="text" placeholder="العنوان الفرعي" 
              value={formData.home_settings?.hero_subtitle_ar || ''}
              onChange={(e) => setFormData({
                ...formData, 
                home_settings: {...formData.home_settings, hero_subtitle_ar: e.target.value}
              })}
              className="w-full px-4 py-2 border rounded-lg" />
            <div className="grid grid-cols-4 gap-4">
              <input type="text" placeholder="عدد البرامج" 
                value={formData.home_settings?.stats_programs || ''}
                onChange={(e) => setFormData({
                  ...formData, 
                  home_settings: {...formData.home_settings, stats_programs: e.target.value}
                })}
                className="px-4 py-2 border rounded-lg" />
              <input type="text" placeholder="أعضاء هيئة التدريس" 
                value={formData.home_settings?.stats_faculty || ''}
                onChange={(e) => setFormData({
                  ...formData, 
                  home_settings: {...formData.home_settings, stats_faculty: e.target.value}
                })}
                className="px-4 py-2 border rounded-lg" />
              <input type="text" placeholder="الطلاب" 
                value={formData.home_settings?.stats_students || ''}
                onChange={(e) => setFormData({
                  ...formData, 
                  home_settings: {...formData.home_settings, stats_students: e.target.value}
                })}
                className="px-4 py-2 border rounded-lg" />
              <input type="text" placeholder="الاتفاقيات" 
                value={formData.home_settings?.stats_agreements || ''}
                onChange={(e) => setFormData({
                  ...formData, 
                  home_settings: {...formData.home_settings, stats_agreements: e.target.value}
                })}
                className="px-4 py-2 border rounded-lg" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle>معلومات الاتصال</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <input type="email" placeholder="البريد الإلكتروني" 
              value={formData.contact_settings?.email || ''}
              onChange={(e) => setFormData({
                ...formData, 
                contact_settings: {...formData.contact_settings, email: e.target.value}
              })}
              className="w-full px-4 py-2 border rounded-lg" />
            <input type="tel" placeholder="الهاتف" 
              value={formData.contact_settings?.phone || ''}
              onChange={(e) => setFormData({
                ...formData, 
                contact_settings: {...formData.contact_settings, phone: e.target.value}
              })}
              className="w-full px-4 py-2 border rounded-lg" />
            <input type="text" placeholder="العنوان" 
              value={formData.contact_settings?.address_ar || ''}
              onChange={(e) => setFormData({
                ...formData, 
                contact_settings: {...formData.contact_settings, address_ar: e.target.value}
              })}
              className="w-full px-4 py-2 border rounded-lg" />
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle>عن الجامعة</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <RichTextEditor label="نبذة عن الجامعة" value={formData.about_ar || ''}
              onChange={(v) => setFormData({...formData, about_ar: v})} rows={5} />
            <RichTextEditor label="الرؤية" value={formData.vision_ar || ''}
              onChange={(v) => setFormData({...formData, vision_ar: v})} rows={4} />
            <RichTextEditor label="الرسالة" value={formData.mission_ar || ''}
              onChange={(v) => setFormData({...formData, mission_ar: v})} rows={4} />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>🎨 خلفيات الصفحات الرئيسية</CardTitle>
            <p className="text-sm text-gray-600 mt-2">صور الخلفية ستظهر مع overlay شفاف بنسبة 50% من لون الجامعة</p>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <ImageUpload 
                label="📖 خلفية صفحة عن الجامعة" 
                value={formData.page_about_bg || ''}
                onChange={(v) => setFormData({...formData, page_about_bg: v})} />
              
              <ImageUpload 
                label="🎓 خلفية صفحة الكليات" 
                value={formData.page_colleges_bg || ''}
                onChange={(v) => setFormData({...formData, page_colleges_bg: v})} />
              
              <ImageUpload 
                label="🏢 خلفية صفحة الإدارات والمراكز" 
                value={formData.page_departments_bg || ''}
                onChange={(v) => setFormData({...formData, page_departments_bg: v})} />
              
              <ImageUpload 
                label="📚 خلفية صفحة البرامج الأكاديمية" 
                value={formData.page_programs_bg || ''}
                onChange={(v) => setFormData({...formData, page_programs_bg: v})} />
              
              <ImageUpload 
                label="👨‍🏫 خلفية صفحة أعضاء هيئة التدريس" 
                value={formData.page_faculty_bg || ''}
                onChange={(v) => setFormData({...formData, page_faculty_bg: v})} />
              
              <ImageUpload 
                label="📰 خلفية صفحة الأخبار والفعاليات" 
                value={formData.page_news_bg || ''}
                onChange={(v) => setFormData({...formData, page_news_bg: v})} />
              
              <ImageUpload 
                label="🖼️ خلفية صفحة معرض الصور والفيديو" 
                value={formData.page_gallery_bg || ''}
                onChange={(v) => setFormData({...formData, page_gallery_bg: v})} />
              
              <ImageUpload 
                label="📞 خلفية صفحة اتصل بنا" 
                value={formData.page_contact_bg || ''}
                onChange={(v) => setFormData({...formData, page_contact_bg: v})} />
            </div>
          </CardContent>
        </Card>

        <div className="flex justify-end">
          <Button type="submit" className="bg-[#0F388E]">
            <Save className="ml-2" />
            حفظ الإعدادات
          </Button>
        </div>
      </form>
    </div>
  );
};

export default SettingsManagement;
